import { createReducer, on } from '@ngrx/store';
import { EmployeeActions } from './employee.actions';
import {
  Employee,
  EmployeeProfile,
  ROLES_NAME,
  RoleTypes,
  TypeData,
} from '@core/interface/employee';
import { Pagination } from '@core/interface/core';

export const EmployeeFeatureKey = 'employee';

export interface EmployeeState {
  employees: Employee[];
  isLoading: boolean;
  isDeleted: boolean;
  inviteLoading: boolean;
  isInvited: boolean;
  isEmployeeByRoleLoading: boolean;
  loadError?: any;
  deleteError?: any;
  inviteError?: any;
  addEditError?: any;
  pagination: Pagination;
  inviteResponse: any;
  employeeByRole: RoleTypes;
  userProfile: EmployeeProfile | null;
  isPasswordChanged: boolean;
  isPasswordChangedLoading: boolean;
  isCompanyChanged: boolean;
  isCompanyChangedLoading: boolean;
  employmentType: {
    data: TypeData[];
    isLoading: boolean;
  };
  workSchedule: {
    data: TypeData[];
    isLoading: boolean;
  };
  workLocation: {
    data: TypeData[];
    isLoading: boolean;
  };
}

export const initialState: EmployeeState = {
  employees: [],
  isLoading: true,
  isDeleted: false,
  isInvited: false,
  inviteResponse: null,
  inviteLoading: false,
  isPasswordChanged: false,
  isPasswordChangedLoading: false,
  isCompanyChanged: false,
  isCompanyChangedLoading: false,
  isEmployeeByRoleLoading: false,
  userProfile: null,
  employeeByRole: {
    [ROLES_NAME.SUPER_ADMIN]: [],
    [ROLES_NAME.ADMIN]: [],
    [ROLES_NAME.CONTRACT_MANAGER]: [],
    [ROLES_NAME.ENGINEER]: [],
    [ROLES_NAME.HR_ADMINISTRATOR]: [],
  },
  pagination: {
    current_page: 1,
    last_page: 1,
    per_page: 10,
    total: 100,
  },
  employmentType: {
    data: [],
    isLoading: false,
  },
  workSchedule: {
    data: [],
    isLoading: false,
  },
  workLocation: {
    data: [],
    isLoading: false,
  },
};

export const EmployeeReducer = createReducer(
  initialState,
  on(EmployeeActions.loadEmployees, (state) => ({ ...state })),
  on(EmployeeActions.loadEmployeesSuccess, (state, { data, pagination }) => ({
    ...state,
    pagination,
    employees: data,
    isLoading: false,
    isDeleted: false,
    loadError: null,
  })),
  on(EmployeeActions.loadEmployeesFailure, (state, { error }) => {
    return { ...state, loadError: error, isLoading: false, isDeleted: false };
  }),
  on(EmployeeActions.addEmployeeSuccess, (state, { data }) => ({
    ...state,
    employees: [...state.employees, data],
    isDeleted: false,
    addEditError: null,
  })),
  on(EmployeeActions.addEmployeeFailure, (state, error) => ({
    ...state,
    isDeleted: false,
    addEditError: error,
  })),
  on(EmployeeActions.updateEmployeeSuccess, (state, { data }) => ({
    ...state,
    employees: state.employees.map((Employee) =>
      data.id == Employee.id ? data : Employee
    ),
    isDeleted: false,
    addEditError: null,
  })),
  on(EmployeeActions.updateEmployeeFailure, (state, { error }) => ({
    ...state,
    isDeleted: false,
    addEditError: error,
  })),
  on(EmployeeActions.deleteEmployeeSuccess, (state, { success, id }) => ({
    ...state,
    isDeleted: success,
    deleteError: null,
    employees: state.employees.filter((Employee) => Employee.id !== id),
  })),
  on(EmployeeActions.deleteEmployeeFailure, (state, error) => ({
    ...state,
    isDeleted: false,
    deleteError: error,
  })),
  on(EmployeeActions.deleteEmployee, (state, error) => ({
    ...state,
    isDeleted: false,
    deleteError: null,
  })),

  on(EmployeeActions.sendInvite, (state) => ({
    ...state,
    inviteLoading: true,
    isInvited: false,
  })),
  on(EmployeeActions.sendInviteSuccess, (state, { data }) => ({
    ...state,
    isDeleted: false,
    inviteError: null,
    inviteLoading: false,
    isInvited: true,
    employees: state.employees.map((Employee) =>
      data.id == Employee.id ? data : Employee
    ),
    inviteResponse: data,
  })),
  on(EmployeeActions.sendInviteFailure, (state, { error }) => {
    return {
      ...state,
      inviteError: error,
      isDeleted: false,
      inviteLoading: false,
      isInvited: false,
    };
  }),
  on(EmployeeActions.loadEmployeesByRole, (state, data) => ({
    ...state,
    employeeByRole: {
      ...state.employeeByRole,
      [data.roleName]: [],
    },
    isEmployeeByRoleLoading: true,
  })),
  on(EmployeeActions.loadEmployeesByRoleSuccess, (state, data) => ({
    ...state,
    employeeByRole: {
      ...state.employeeByRole,
      [data.roleName]: [...data.employees.data],
    },
    isDeleted: false,
    loadError: null,
    isEmployeeByRoleLoading: false,
  })),
  on(EmployeeActions.loadEmployeesByRoleFailure, (state, data) => {
    return {
      ...state,
      isDeleted: false,
      employeeByRole: {
        ...state.employeeByRole,
        [data.roleName]: [], // Clear the employees for this role on failure
      },
      isEmployeeByRoleLoading: false,
    };
  }),

  on(EmployeeActions.employeeProfile, (state) => ({
    ...state,
    userProfile: null,
  })),
  on(EmployeeActions.employeeProfileSuccess, (state, { data }) => ({
    ...state,
    isDeleted: false,
    userProfile: data,
  })),
  on(EmployeeActions.employeeProfileFailure, (state, { error }) => {
    return {
      ...state,
      loadError: error,
      isDeleted: false,
    };
  }),
  on(EmployeeActions.changePassword, (state) => ({
    ...state,
    isPasswordChanged: false,
    isPasswordChangedLoading: true,
  })),
  on(EmployeeActions.changePasswordSuccess, (state, { success }) => ({
    ...state,
    isDeleted: false,
    isPasswordChangedLoading: false,
    isPasswordChanged: success,
  })),
  on(EmployeeActions.changePasswordFailure, (state) => {
    return {
      ...state,
      isDeleted: false,
      isPasswordChangedLoading: false,
      isPasswordChanged: false,
    };
  }),
  on(EmployeeActions.resetFlags, (state) => {
    return {
      ...state,
      isInvited: false,
      isPasswordChanged: false,
      isCompanyChanged: false,
      employeeByRole: {
        [ROLES_NAME.SUPER_ADMIN]: [],
        [ROLES_NAME.ADMIN]: [],
        [ROLES_NAME.CONTRACT_MANAGER]: [],
        [ROLES_NAME.ENGINEER]: [],
        [ROLES_NAME.HR_ADMINISTRATOR]: [],
      },
    };
  }),

  on(EmployeeActions.loadEmploymentType, (state) => ({
    ...state,
    employmentType: { ...state.employmentType, isLoading: true },
  })),
  on(EmployeeActions.loadEmploymentTypeSuccess, (state, { data }) => ({
    ...state,
    employmentType: {
      ...state.employmentType,
      data: data,
      isLoading: false,
    },
  })),
  on(EmployeeActions.loadEmploymentTypeFailure, (state) => ({
    ...state,
    employmentType: { ...state.employmentType, isLoading: false },
  })),
  on(EmployeeActions.addEmploymentTypeSuccess, (state, { data }) => ({
    ...state,
    employmentType: {
      ...state.employmentType,
      data: [...state.employmentType.data, data],
    },
  })),
  on(EmployeeActions.updateEmploymentTypeSuccess, (state, { data }) => ({
    ...state,
    employmentType: {
      ...state.employmentType,
      data: state.employmentType.data.map((type) =>
        data.id == type.id ? data : type
      ),
    },
  })),

  on(EmployeeActions.loadWorkSchedule, (state) => ({
    ...state,
    workSchedule: { ...state.workSchedule, isLoading: true },
  })),
  on(EmployeeActions.loadWorkScheduleSuccess, (state, { data }) => ({
    ...state,
    workSchedule: {
      ...state.workSchedule,
      data: data,
      isLoading: false,
    },
  })),
  on(EmployeeActions.loadWorkScheduleFailure, (state) => ({
    ...state,
    workSchedule: { ...state.workSchedule, isLoading: false },
  })),
  on(EmployeeActions.addWorkScheduleSuccess, (state, { data }) => ({
    ...state,
    workSchedule: {
      ...state.workSchedule,
      data: [...state.workSchedule.data, data],
    },
  })),
  on(EmployeeActions.updateWorkScheduleSuccess, (state, { data }) => ({
    ...state,
    workSchedule: {
      ...state.workSchedule,
      data: state.workSchedule.data.map((type) =>
        data.id == type.id ? data : type
      ),
    },
  })),

  on(EmployeeActions.loadWorkLocation, (state) => ({
    ...state,
    workLocation: { ...state.workLocation, isLoading: true },
  })),
  on(EmployeeActions.loadWorkLocationSuccess, (state, { data }) => ({
    ...state,
    workLocation: {
      ...state.workLocation,
      data: data,
      isLoading: false,
    },
  })),
  on(EmployeeActions.loadWorkLocationFailure, (state) => ({
    ...state,
    workLocation: { ...state.workLocation, isLoading: false },
  })),
  on(EmployeeActions.addWorkLocationSuccess, (state, { data }) => ({
    ...state,
    workLocation: {
      ...state.workLocation,
      data: [...state.workLocation.data, data],
    },
  })),
  on(EmployeeActions.updateWorkLocationSuccess, (state, { data }) => ({
    ...state,
    workLocation: {
      ...state.workLocation,
      data: state.workLocation.data.map((type) =>
        data.id == type.id ? data : type
      ),
    },
  })),

  on(EmployeeActions.changeCompany, (state) => ({
    ...state,
    isCompanyChanged: false,
    isCompanyChangedLoading: true,
  })),
  on(EmployeeActions.changeCompanySuccess, (state, { success }) => ({
    ...state,
    isDeleted: false,
    isCompanyChangedLoading: false,
    isCompanyChanged: success,
  })),
  on(EmployeeActions.changeCompanyFailure, (state) => {
    return {
      ...state,
      isDeleted: false,
      isCompanyChangedLoading: false,
      isCompanyChanged: false,
    };
  })
);
